/**
 * The pictures hanging off a draft — shared by the report panel and by a reply
 * in an existing thread, which need the same add / mark up / caption / remove
 * behaviour over the same object URLs.
 */
import { useCallback, useRef, useState } from "react";

export interface DraftPicture {
	key: number;
	blob: Blob;
	url: string;
	caption: string;
}

export interface DraftPictures {
	pictures: DraftPicture[];
	/** The picture currently open in the editor, if any. */
	editing: DraftPicture | null;
	/** Returns the new picture's key, so the caller can open it for markup. */
	add: (blob: Blob) => number;
	remove: (key: number) => void;
	setCaption: (key: number, caption: string) => void;
	openEditor: (key: number) => void;
	closeEditor: () => void;
	/** Marks replace the picture they were drawn on: the original is discarded. */
	replaceEdited: (flattened: Blob, caption: string) => void;
	/** Frees every object URL and empties the draft. */
	clear: () => void;
}

export const useDraftPictures = (): DraftPictures => {
	const [pictures, setPictures] = useState<DraftPicture[]>([]);
	const [editingKey, setEditingKey] = useState<number | null>(null);
	const nextKey = useRef(1);

	const add = useCallback((blob: Blob): number => {
		const key = nextKey.current++;
		setPictures((previous) => [
			...previous,
			{ key, blob, url: URL.createObjectURL(blob), caption: "" },
		]);
		return key;
	}, []);

	const remove = useCallback((key: number) => {
		setPictures((previous) => {
			previous
				.filter((picture) => picture.key === key)
				.forEach((picture) => URL.revokeObjectURL(picture.url));
			return previous.filter((picture) => picture.key !== key);
		});
	}, []);

	const setCaption = useCallback((key: number, caption: string) => {
		setPictures((previous) =>
			previous.map((picture) =>
				picture.key === key ? { ...picture, caption } : picture
			)
		);
	}, []);

	const replaceEdited = useCallback(
		(flattened: Blob, caption: string) => {
			setPictures((previous) =>
				previous.map((picture) => {
					if (picture.key !== editingKey) return picture;
					URL.revokeObjectURL(picture.url);
					return {
						...picture,
						blob: flattened,
						url: URL.createObjectURL(flattened),
						caption: caption || picture.caption,
					};
				})
			);
			setEditingKey(null);
		},
		[editingKey]
	);

	const clear = useCallback(() => {
		setPictures((previous) => {
			previous.forEach((picture) => URL.revokeObjectURL(picture.url));
			return [];
		});
		setEditingKey(null);
	}, []);

	return {
		pictures,
		editing: pictures.find((picture) => picture.key === editingKey) ?? null,
		add,
		remove,
		setCaption,
		openEditor: setEditingKey,
		closeEditor: () => setEditingKey(null),
		replaceEdited,
		clear,
	};
};
